import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Close.js';
import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import type { Theme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiSparkleDefaultIcon } from '../../Icons/AiSparkleDefaultIcon';
import {
  atlasAuthCheckboxCardSx,
  atlasAuthCheckboxControlSx,
  atlasAuthCheckboxRowSx,
  atlasButtonMdSx,
  atlasFontTextBody,
  atlasFontTextMd,
  atlasFontTitleH3Lg,
  atlasLensModalShell,
  atlasPrimaryDisabledButtonSx,
  atlasSpacing,
  atlasSpinnerSizeMd,
  type AtlasLensTheme,
} from '../../Form/atlasLensStyles';
import {
  ATLAS_LENS_COMPONENT_VARS,
  ATLAS_LENS_SEMANTIC_VARS,
} from '../../Form/atlasLensTokens';
import { OnboardingCheckbox } from '../OnboardingCheckbox';
import { useOnboardingLens } from '../onboardingLens';
import { useOnboardingNestedDialog } from '../useOnboardingNestedDialog';

/** CEE-992 — Welcome page header (H1 Billboard title row). */
export function OnboardingWelcomePageHeader() {
  const { t } = useTranslation();
  const { layout, typography, typeDefault, accentRedRequired } = useOnboardingLens();
  const { pageHeader } = layout.welcomeStep1;

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'flex-start',
        width: '100%',
        height: pageHeader.height,
        flexShrink: 0,
        px: pageHeader.paddingX,
        pt: pageHeader.paddingTop,
        pb: 0,
      }}
    >
      <Typography
        component="h1"
        sx={{
          ...typography.h1Billboard,
          color: typeDefault,
          m: 0,
        }}
      >
        {t('onboarding.wizard.step1.title_prefix')}{' '}
        <Box component="span" sx={{ color: accentRedRequired }}>
          {t('onboarding.wizard.step1.title_highlight')}
        </Box>
      </Typography>
    </Box>
  );
}

export type AiPreferenceDialogLabelKeys = {
  title: string;
  body: string;
  /** Second body paragraph (same style as body). */
  bodyHint?: string;
  /** Optional muted notice (e.g. Legal statement pending). */
  pendingHint?: string;
  checkbox?: string;
  back: string;
  confirm: string;
};

type OnboardingAiPreferenceDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  modalZIndex?: number;
  isSubmitting?: boolean;
  /** When false, authorize variant shows body only (no authorization checkbox). Default true. */
  requireAuthorization?: boolean;
  /**
   * Force Confirm disabled (e.g. CEE-1208 Legal statement not ready).
   * Independent of the authorization checkbox.
   */
  forceConfirmDisabled?: boolean;
  /** Override default onboarding copy (e.g. Settings turn on/off dialogs). */
  labelKeys?: AiPreferenceDialogLabelKeys;
} & (
  | { variant: 'authorize' }
  | { variant: 'opt_out' }
);

const ONBOARDING_AI_PREFERENCE_LABEL_KEYS: Record<
  OnboardingAiPreferenceDialogProps['variant'],
  AiPreferenceDialogLabelKeys
> = {
  authorize: {
    title: 'onboarding.wizard.step1.ai_auth_dialog.title',
    body: 'onboarding.wizard.step1.ai_auth_dialog.body',
    checkbox: 'onboarding.wizard.step1.ai_auth_dialog.checkbox',
    back: 'onboarding.wizard.step1.ai_auth_dialog.back',
    confirm: 'onboarding.wizard.step1.ai_auth_dialog.confirm',
  },
  opt_out: {
    title: 'onboarding.wizard.step1.ai_opt_out_dialog.title',
    body: 'onboarding.wizard.step1.ai_opt_out_dialog.body',
    bodyHint: 'onboarding.wizard.step1.ai_opt_out_dialog.settings_hint',
    back: 'onboarding.wizard.step1.ai_auth_dialog.back',
    confirm: 'onboarding.wizard.step1.ai_opt_out_dialog.confirm',
  },
};

/** CEE-992 — authorization and opt-out confirmation modals (onboarding + settings). */
export function OnboardingAiPreferenceDialog(props: OnboardingAiPreferenceDialogProps) {
  const {
    open,
    onClose,
    onConfirm,
    variant,
    modalZIndex,
    isSubmitting = false,
    requireAuthorization = true,
    forceConfirmDisabled = false,
    labelKeys,
  } = props;
  const { t } = useTranslation();
  const theme = useTheme() as Theme & LensTheme;
  const atlasTheme = theme as AtlasLensTheme;
  const shell = atlasLensModalShell(atlasTheme);
  const [authorized, setAuthorized] = useState(false);
  const nestedDialog = useOnboardingNestedDialog(modalZIndex);
  const resolvedModalZIndex = nestedDialog.rootZIndex;
  const labels = labelKeys ?? ONBOARDING_AI_PREFERENCE_LABEL_KEYS[variant];

  useEffect(() => {
    if (!open) {
      setAuthorized(false);
    }
  }, [open]);

  const handleClose = () => {
    if (isSubmitting) return;
    setAuthorized(false);
    onClose();
  };

  const handleConfirm = () => {
    if (isSubmitting || forceConfirmDisabled) return;
    if (variant === 'authorize' && requireAuthorization && !authorized) return;
    setAuthorized(false);
    onConfirm();
  };

  const showAuthorizationCheckbox = variant === 'authorize' && requireAuthorization;
  const confirmDisabled =
    isSubmitting ||
    forceConfirmDisabled ||
    (showAuthorizationCheckbox && !authorized);
  const backButtonMinWidth = `calc(${atlasSpacing(atlasTheme, '5')} * 2 + ${atlasSpacing(atlasTheme, '3')} - ${atlasSpacing(atlasTheme, '0_5')})`;
  // Hug content (nowrap comes from atlasButtonMdSx). Fixed *8 minWidth overflowed the modal footer.
  const confirmButtonMinWidth = 'auto';

  const bodyTypographySx = {
    m: 0,
    ...atlasFontTextBody(atlasTheme),
    color: ATLAS_LENS_SEMANTIC_VARS.typeDefault,
  };

  const checkboxLabelSlotProps = {
    typography: {
      component: 'span' as const,
      sx: {
        ...atlasFontTextMd(atlasTheme),
        color: ATLAS_LENS_SEMANTIC_VARS.typeDefault,
      },
    },
  };

  const sectionPaddingSx = {
    pl: shell.content.px,
    pr: shell.content.px,
    pb: shell.content.pb,
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : handleClose}
      maxWidth={false}
      disablePortal={nestedDialog.disablePortal}
      container={nestedDialog.container}
      sx={nestedDialog.sx}
      aria-labelledby="onboarding-ai-preference-dialog-title"
      aria-describedby="onboarding-ai-preference-dialog-description"
      slotProps={{
        root: {
          sx: nestedDialog.nested ? undefined : { zIndex: resolvedModalZIndex },
        },
        backdrop: { sx: { backgroundColor: ATLAS_LENS_SEMANTIC_VARS.backgroundBackdrop } },
        paper: { sx: shell.paper },
      }}
    >
      <DialogTitle id="onboarding-ai-preference-dialog-title" component="div" sx={shell.header}>
        <Typography
          component="span"
          sx={{
            flex: 1,
            m: 0,
            pr: atlasSpacing(atlasTheme, '1'),
            ...atlasFontTitleH3Lg(atlasTheme),
            color: ATLAS_LENS_SEMANTIC_VARS.typeDefault,
          }}
        >
          {t(labels.title)}
        </Typography>
        <IconButton
          aria-label={t('actions.close')}
          onClick={handleClose}
          disabled={isSubmitting}
          sx={shell.closeButton}
        >
          <CloseIcon style={{ fontSize: shell.closeIconSize }} />
        </IconButton>
      </DialogTitle>

      <DialogContent id="onboarding-ai-preference-dialog-description" sx={{ px: 0, py: 0 }}>
        {variant === 'authorize' ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: showAuthorizationCheckbox ? atlasSpacing(atlasTheme, '3') : 0,
              ...(showAuthorizationCheckbox
                ? { pl: shell.content.px, pr: shell.content.px, pb: shell.content.pb }
                : sectionPaddingSx),
            }}
          >
            <Typography component="p" sx={bodyTypographySx}>{t(labels.body)}</Typography>
            {labels.bodyHint ? (
              <Typography component="p" sx={bodyTypographySx}>
                {t(labels.bodyHint)}
              </Typography>
            ) : null}
            {labels.pendingHint ? (
              <Typography
                component="p"
                sx={{
                  ...bodyTypographySx,
                  color: ATLAS_LENS_SEMANTIC_VARS.typeMuted,
                }}
              >
                {t(labels.pendingHint)}
              </Typography>
            ) : null}
            {showAuthorizationCheckbox ? (
              <Box sx={atlasAuthCheckboxCardSx(atlasTheme)}>
                <FormControlLabel
                  control={
                    <OnboardingCheckbox
                      checked={authorized}
                      onChange={(e) => setAuthorized(e.target.checked)}
                      disabled={isSubmitting || forceConfirmDisabled}
                      sx={atlasAuthCheckboxControlSx()}
                    />
                  }
                  label={t(labels.checkbox ?? ONBOARDING_AI_PREFERENCE_LABEL_KEYS.authorize.checkbox!)}
                  slotProps={checkboxLabelSlotProps}
                  sx={{
                    ...atlasAuthCheckboxRowSx(atlasTheme),
                    width: '100%',
                    '& .MuiFormControlLabel-label': {
                      flex: 1,
                      minWidth: 0,
                      minHeight: ATLAS_LENS_COMPONENT_VARS.checkboxHeight,
                      display: 'flex',
                      alignItems: 'center',
                      m: 0,
                    },
                  }}
                />
              </Box>
            ) : null}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: `calc(${atlasSpacing(atlasTheme, '1')} + ${atlasSpacing(atlasTheme, 'px')})`,
              ...sectionPaddingSx,
            }}
          >
            <Typography component="p" sx={bodyTypographySx}>{t(labels.body)}</Typography>
            {labels.bodyHint ? (
              <Typography component="p" sx={bodyTypographySx}>
                {t(labels.bodyHint)}
              </Typography>
            ) : null}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={shell.footer}>
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={isSubmitting}
          sx={{
            ...atlasButtonMdSx(atlasTheme),
            minWidth: backButtonMinWidth,
            color: ATLAS_LENS_SEMANTIC_VARS.typeDefault,
            borderColor: ATLAS_LENS_SEMANTIC_VARS.typeDefault,
            backgroundColor: ATLAS_LENS_SEMANTIC_VARS.surfaceDefault,
            '&:hover': {
              borderColor: ATLAS_LENS_SEMANTIC_VARS.typeDefault,
              backgroundColor: ATLAS_LENS_SEMANTIC_VARS.surfaceDefault,
            },
          }}
        >
          {t(labels.back)}
        </Button>
        <Button
          variant="contained"
          size="medium"
          onClick={handleConfirm}
          disabled={confirmDisabled}
          aria-busy={isSubmitting}
          sx={{
            ...atlasButtonMdSx(atlasTheme),
            minWidth: confirmButtonMinWidth,
            maxWidth: '100%',
            ...(confirmDisabled
              ? atlasPrimaryDisabledButtonSx()
              : {
                  '&:hover': {
                    boxShadow: 'none',
                  },
                }),
          }}
        >
          {isSubmitting ? (
            <CircularProgress
              size={atlasSpinnerSizeMd(atlasTheme)}
              aria-label={t('loading')}
              sx={{ color: 'inherit' }}
            />
          ) : (
            t(labels.confirm)
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** CEE-992 — Welcome left column (section header + AI alert). */
export function OnboardingStepWelcome() {
  const { t } = useTranslation();
  const { layout, typography, typeDefault, typeMuted, accentRedRequired } = useOnboardingLens();
  const { leftColumn, alert } = layout.welcomeStep1;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: leftColumn.sectionGap,
        width: '100%',
        maxWidth: leftColumn.width,
        height: '100%',
        px: leftColumn.paddingX,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: leftColumn.titleSubtitleGap,
          width: '100%',
        }}
      >
        <Typography component="h2" sx={{ ...typography.h4Md, color: typeDefault, m: 0 }}>
          {t('onboarding.wizard.step1.personalize_title')}
        </Typography>
        <Typography sx={{ ...typography.textMd, color: typeDefault, m: 0 }}>
          {t('onboarding.wizard.step1.personalize_subtitle')}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: alert.iconGap,
          width: '100%',
          py: alert.paddingY,
          px: alert.paddingX,
          borderRadius: alert.borderRadius,
          backgroundColor: alert.backgroundColor,
          boxSizing: 'border-box',
        }}
      >
        <Box
          aria-hidden
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: alert.iconSize,
            height: alert.iconSize,
            flexShrink: 0,
            color: alert.contentColor,
            '& > dil-ai-sparkle-default-icon': {
              display: 'block',
              width: '100%',
              height: '100%',
            },
          }}
        >
          <AiSparkleDefaultIcon size="md" iconColor={alert.contentColor} />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: alert.messagePaddingY,
            minWidth: 0,
            flex: 1,
          }}
        >
          <Typography sx={{ ...typography.textMdEmphasis, color: alert.contentColor, m: 0 }}>
            {t('onboarding.wizard.step1.ai_info_title')}
          </Typography>
          <Typography sx={{ ...typography.textMd, color: alert.contentColor, m: 0 }}>
            {t('onboarding.wizard.step1.ai_info_body')}
          </Typography>
        </Box>
      </Box>

      <Typography
        role="note"
        sx={{ ...typography.textSm, color: typeMuted, m: 0, width: '100%' }}
      >
        {t('onboarding.wizard.step1.required_footnote_prefix')}
        <Box component="span" sx={{ color: accentRedRequired }} aria-hidden="true">
          *
        </Box>
        {t('onboarding.wizard.step1.required_footnote_suffix')}
      </Typography>
    </Box>
  );
}
